import React, { useState, useEffect } from 'react';
import { tippspielAdminAPI } from '../../services/api';

const TippspielConnectionSettings = () => {
  const [settings, setSettings] = useState(null);
  const [projects, setProjects] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [apiStatus, setApiStatus] = useState('unknown');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, projectsRes, statusRes] = await Promise.all([
        tippspielAdminAPI.getSettings(),
        tippspielAdminAPI.getMfleagueProjects().catch(() => ({ data: { projects: [] } })),
        tippspielAdminAPI.getSyncStatus(),
      ]);
      setSettings(settingsRes.data);
      setProjects(projectsRes.data.projects || []);
      setApiStatus(projectsRes.data.mfleagueApiStatus || statusRes.data.mfleagueApiStatus || 'unknown');
    } catch (err) {
      setError(err.response?.data?.message || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onProjectChange = async (projectId) => {
    const project = projects.find((p) => p._id === projectId);
    setSettings((s) => ({
      ...s,
      activeMfleagueProjectId: projectId,
      activeMfleagueProjectName: project?.name || '',
    }));
    if (projectId) {
      const [sRes, rRes] = await Promise.all([
        tippspielAdminAPI.getProjectSeasons(projectId).catch(() => ({ data: [] })),
        tippspielAdminAPI.getProjectCompetitions(projectId).catch(() => ({ data: [] })),
      ]);
      setSeasons(sRes.data || []);
      setRounds(rRes.data || []);
    }
  };

  useEffect(() => {
    if (!settings?.activeMfleagueProjectId || projects.length === 0) return;
    const loadMeta = async () => {
      const projectId = settings.activeMfleagueProjectId;
      const [sRes, rRes] = await Promise.all([
        tippspielAdminAPI.getProjectSeasons(projectId).catch(() => ({ data: [] })),
        tippspielAdminAPI.getProjectCompetitions(projectId).catch(() => ({ data: [] })),
      ]);
      setSeasons(sRes.data || []);
      setRounds(rRes.data || []);
    };
    loadMeta();
  }, [settings?.activeMfleagueProjectId, projects.length]);

  const handleSave = async (confirmProjectChange = false) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await tippspielAdminAPI.saveMfleagueProject({
        activeMfleagueProjectId: settings.activeMfleagueProjectId,
        activeMfleagueProjectName: settings.activeMfleagueProjectName,
        activeSeasonId: settings.activeSeasonId || '',
        activeSeasonName: settings.activeSeasonName || '',
        activeCompetitionId: settings.activeCompetitionId || '',
        activeCompetitionName: settings.activeCompetitionName || '',
        syncEnabled: settings.syncEnabled,
        syncIntervalMinutes: settings.syncIntervalMinutes,
        confirmProjectChange,
      });
      setSettings(res.data);
      setMessage('Einstellungen gespeichert.');
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`${err.response.data.message}\n\nTrotzdem fortfahren?`)) {
          await handleSave(true);
          return;
        }
      }
      setError(err.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    setMessage('');
    try {
      const res = await tippspielAdminAPI.syncProject({ mode: 'incremental' });
      setMessage(`Sync OK: ${res.data.matchesUpserted} Spiele, ${res.data.tipsScored} Tipps gewertet.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><span className="loading loading-spinner loading-lg text-wacker" /></div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="section-title"><span>Verbindung & Projekt</span></h2>

      {error && <div className="alert-error-dark">{error}</div>}
      {message && <div className="alert-success-dark">{message}</div>}

      <div className="glass-card p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium text-white/80">API-Status:</span>
          <span className={`text-xs px-2 py-1 rounded-full ${
            apiStatus === 'ok' ? 'bg-wacker/20 text-wacker-light' : apiStatus === 'error' ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-white/60'
          }`}
          >
            {apiStatus}
          </span>
          {settings?.lastSyncAt && (
            <span className="text-sm text-white/45 ml-auto">
              Letzter Sync: {new Date(settings.lastSyncAt).toLocaleString('de-DE')}
            </span>
          )}
        </div>

        <div>
          <label className="label-dark">MFLeague-Projekt</label>
          <select
            className="admin-select"
            value={settings?.activeMfleagueProjectId || ''}
            onChange={(e) => onProjectChange(e.target.value)}
          >
            <option value="">Projekt wählen…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-dark">Saison (optional)</label>
            <select
              className="admin-select"
              value={settings?.activeSeasonId || ''}
              onChange={(e) => {
                const s = seasons.find((x) => x._id === e.target.value);
                setSettings({ ...settings, activeSeasonId: e.target.value, activeSeasonName: s?.name || '' });
              }}
            >
              <option value="">Alle / keine</option>
              {seasons.map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-dark">Spieltag (optional)</label>
            <select
              className="admin-select"
              value={settings?.activeCompetitionId || ''}
              onChange={(e) => {
                const r = rounds.find((x) => x._id === e.target.value);
                setSettings({ ...settings, activeCompetitionId: e.target.value, activeCompetitionName: r?.name || '' });
              }}
            >
              <option value="">Alle Spieltage</option>
              {rounds.map((r) => (
                <option key={r._id} value={r._id}>{r.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 cursor-pointer text-white/80">
            <input
              type="checkbox"
              className="admin-checkbox"
              checked={Boolean(settings?.syncEnabled)}
              onChange={(e) => setSettings({ ...settings, syncEnabled: e.target.checked })}
            />
            Auto-Sync aktiv
          </label>
          <div>
            <label className="label-dark">Sync-Intervall (Minuten)</label>
            <input
              type="number"
              className="admin-input py-2"
              min={5}
              value={settings?.syncIntervalMinutes || 60}
              onChange={(e) => setSettings({ ...settings, syncIntervalMinutes: parseInt(e.target.value, 10) || 60 })}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button type="button" className="btn-wacker text-sm py-2 px-4" onClick={() => handleSave()} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
          <button type="button" className="btn-wacker-outline text-sm py-2 px-4" onClick={handleSync} disabled={syncing || !settings?.activeMfleagueProjectId}>
            {syncing ? 'Sync läuft…' : 'Jetzt synchronisieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TippspielConnectionSettings;
